import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';

interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render?: (item: T) => React.ReactNode;
}

interface SetupDataTableProps<T> {
  brand: BrandConfig;
  Icons: IconsType;
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  getRowId: (item: T) => string;
}

function SetupDataTable<T>({
  brand,
  columns,
  data,
  isLoading = false,
  onRowClick,
  emptyMessage = 'No data found',
  getRowId,
}: SetupDataTableProps<T>) {
  if (isLoading) {
    return (
      <div
        className="bg-white rounded-lg border overflow-hidden"
        style={{ borderColor: brand.colors.border }}
      >
        <div className="p-8 text-center">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto"
            style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
          />
          <p className="mt-4 text-sm" style={{ color: brand.colors.textLight }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        className="bg-white rounded-lg border overflow-hidden"
        style={{ borderColor: brand.colors.border }}
      >
        <div className="p-8 text-center">
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            {emptyMessage}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border overflow-hidden"
      style={{ borderColor: brand.colors.border }}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  style={{
                    color: brand.colors.textLight,
                    width: column.width,
                  }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: brand.colors.border }}>
            {data.map((item) => (
              <tr
                key={getRowId(item)}
                onClick={() => onRowClick?.(item)}
                className={`transition-colors ${
                  onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-4 py-3 text-sm"
                    style={{ color: brand.colors.text }}
                  >
                    {column.render
                      ? column.render(item)
                       
                      : (item as any)[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SetupDataTable;
